import React, { useCallback, useState } from 'react';
import Page from 'components/page/Page';
import { AuthService } from 'auth/AuthService';
import Environment from 'Environment';

interface LogEntry {
  time: string;
  action: string;
  status: 'success' | 'error' | 'info';
  detail?: string;
}

const DevTools = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState<string | null>(null);

  const addLog = useCallback((action: string, status: LogEntry['status'], detail?: string) => {
    const time = new Date().toLocaleTimeString('nb-NO');
    setLogs((prev) => [{ time, action, status, detail }, ...prev]);
  }, []);

  const refreshAllCaches = useCallback(async () => {
    setLoading('refresh');
    addLog('Refresh All Caches', 'info', 'Starter cache refresh...');
    try {
      const token = await AuthService.getAccessTokenSilent();
      const response = await fetch(`${Environment.ApiBaseUrl}/api/dev-tools/refresh-all-caches`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const text = await response.text();
        addLog('Refresh All Caches', 'error', `HTTP ${response.status}: ${text}`);
      } else {
        const data = await response.json();
        addLog('Refresh All Caches', 'success', data.message);
      }
    } catch (e) {
      addLog('Refresh All Caches', 'error', e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(null);
    }
  }, [addLog]);

  const clearMsalStorage = useCallback(() => {
    setLoading('clear');
    try {
      AuthService.clearStorageValues();
      addLog('Clear MSAL Storage', 'success', 'Alle msal.* og b2c_* verdier fjernet fra localStorage.');
    } catch (e) {
      addLog('Clear MSAL Storage', 'error', e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(null);
    }
  }, [addLog]);

  const diagnosePolicyCache = useCallback(async () => {
    setLoading('diagnose');
    addLog('Diagnose Policy Cache', 'info', 'Henter diagnostikkrapport...');
    try {
      const token = await AuthService.getAccessTokenSilent();
      const response = await fetch(`${Environment.ApiBaseUrl}/api/dev-tools/diagnose-policy-cache`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        const text = await response.text();
        addLog('Diagnose Policy Cache', 'error', `HTTP ${response.status}: ${text}`);
      } else {
        const data = await response.json();
        const summary = Object.entries(data.entityTypes as Record<string, { total: number; unchanged: number; changed: number; new: number; changedKeys: string[] }>)
          .map(([type, d]) => `${type}: ${d.unchanged} uendret, ${d.changed} endret, ${d.new} nye (totalt ${d.total})`)
          .join(' | ');
        const hasChanges =
          data.entityTypes &&
          Object.values(data.entityTypes as Record<string, { changed: number }>).some((d) => d.changed > 0);
        const status: LogEntry['status'] = hasChanges ? 'error' : 'success';
        addLog('Diagnose Policy Cache', status, summary);
      }
    } catch (e) {
      addLog('Diagnose Policy Cache', 'error', e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(null);
    }
  }, [addLog]);

  const checkTokenRefresh = useCallback(async () => {
    setLoading('check');
    addLog('Check Token Refresh', 'info', 'Henter token og sjekker mot lokal policy-api...');
    try {
      const token = await AuthService.getAccessTokenSilent();
      if (!token) {
        addLog('Check Token Refresh', 'error', 'Ingen token tilgjengelig.');
        return;
      }

      // Dekod JWT for å hente objectId (oid) og utstedelsestidspunkt (iat)
      const parts = token.split('.');
      if (parts.length !== 3) {
        addLog('Check Token Refresh', 'error', 'Ugyldig JWT-format.');
        return;
      }
      // base64url-dekoding: erstatt -/_ med +/ og legg til padding
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      let payload: Record<string, unknown>;
      try {
        payload = JSON.parse(atob(padded));
      } catch {
        addLog('Check Token Refresh', 'error', 'Kunne ikke dekode JWT-payload.');
        return;
      }
      const objectId = String(payload.oid || payload.sub || '');
      const iat = Number(payload.iat);

      if (!objectId) {
        addLog('Check Token Refresh', 'error', 'Fant ikke objectId (oid/sub) i token.');
        return;
      }
      if (!iat || isNaN(iat)) {
        addLog('Check Token Refresh', 'error', 'Fant ikke iat (issued at) i token.');
        return;
      }

      const tokenIssuedAt = new Date(iat * 1000).toISOString();
      addLog('Check Token Refresh', 'info', `objectId=${objectId}, tokenIssuedAt=${tokenIssuedAt}`);

      const params = new URLSearchParams({
        objectId,
        refreshTokenIssuedOnDateTime: tokenIssuedAt
      });
      const response = await fetch(`https://localhost:5005/api/devtools/check-token-refresh?${params}`);

      if (!response.ok) {
        const text = await response.text();
        addLog('Check Token Refresh', 'error', `HTTP ${response.status}: ${text}`);
      } else {
        const data = await response.json();
        const status = data.wouldTrigger409 ? 'error' : 'success';
        const label = data.wouldTrigger409 ? '409 VILLE TRIGGES' : '200 OK (ingen endring)';
        const detail = `${label} | latestChange=${data.latestChange ?? 'null'} | tokenIssuedAt=${data.tokenIssuedAt} | diff=${data.diffSeconds != null ? data.diffSeconds.toFixed(1) + 's' : 'N/A'}`;
        addLog('Check Token Refresh', status, detail);
      }
    } catch (e) {
      addLog('Check Token Refresh', 'error', e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(null);
    }
  }, [addLog]);

  const forceTokenRefresh = useCallback(async () => {
    setLoading('token');
    addLog('Force Token Refresh', 'info', 'Tvinger token refresh med forceRefresh=true...');
    try {
      const token = await AuthService.getAccessTokenSilent(true);
      if (token) {
        addLog('Force Token Refresh', 'success', 'Nytt token mottatt.');
      } else {
        addLog('Force Token Refresh', 'error', 'Ingen token returnert.');
      }
    } catch (e) {
      addLog('Force Token Refresh', 'error', e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(null);
    }
  }, [addLog]);

  const statusColor = (status: LogEntry['status']) => {
    switch (status) {
      case 'success':
        return 'text-success';
      case 'error':
        return 'text-danger';
      default:
        return 'text-info';
    }
  };

  return (
    <Page header='Dev Tools' title='Dev Tools'>
      <div className='container mt-4'>
        <div className='alert alert-warning'>
          Denne siden er kun tilgjengelig lokalt. Den eksisterer ikke i deployerte miljøer.
        </div>

        <div className='row mb-4'>
          <div className='col-md-4 mb-3'>
            <div className='card h-100'>
              <div className='card-body'>
                <h5 className='card-title'>Refresh All Caches</h5>
                <p className='card-text'>Kjører WriteUserCache for alle brukere, tilsvarende nattlig cache refresh-jobb.</p>
                <button
                  className='btn btn-primary'
                  onClick={refreshAllCaches}
                  disabled={loading !== null}
                >
                  {loading === 'refresh' ? 'Kjører...' : 'Refresh All Caches'}
                </button>
              </div>
            </div>
          </div>

          <div className='col-md-4 mb-3'>
            <div className='card h-100'>
              <div className='card-body'>
                <h5 className='card-title'>Clear MSAL Storage</h5>
                <p className='card-text'>Sletter alle msal.* og b2c_* verdier fra localStorage. Neste API-kall tvinger token refresh.</p>
                <button
                  className='btn btn-warning'
                  onClick={clearMsalStorage}
                  disabled={loading !== null}
                >
                  {loading === 'clear' ? 'Sletter...' : 'Clear MSAL Storage'}
                </button>
              </div>
            </div>
          </div>

          <div className='col-md-4 mb-3'>
            <div className='card h-100'>
              <div className='card-body'>
                <h5 className='card-title'>Force Token Refresh</h5>
                <p className='card-text'>Henter nytt access token via refresh token (forceRefresh=true). Verifiserer token refresh-flyten.</p>
                <button
                  className='btn btn-info'
                  onClick={forceTokenRefresh}
                  disabled={loading !== null}
                >
                  {loading === 'token' ? 'Henter...' : 'Force Token Refresh'}
                </button>
              </div>
            </div>
          </div>

          <div className='col-md-4 mb-3'>
            <div className='card h-100'>
              <div className='card-body'>
                <h5 className='card-title'>Diagnose Policy Cache</h5>
                <p className='card-text'>Sammenligner gjeldende policy-cache mot databasen. Viser hvilke entiteter som har endringer.</p>
                <button
                  className='btn btn-secondary'
                  onClick={diagnosePolicyCache}
                  disabled={loading !== null}
                >
                  {loading === 'diagnose' ? 'Analyserer...' : 'Diagnose Policy Cache'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className='row mb-4'>
          <div className='col-md-6 mb-3'>
            <div className='card h-100 border-danger'>
              <div className='card-body'>
                <h5 className='card-title'>Check Token Refresh (Policy API)</h5>
                <p className='card-text'>
                  Kaller lokal policy-api med samme logikk som B2C sin token-refresh-claims.
                  Sjekker om LastChangeTime (composite av UserCache, Application, Role, Permission, AdGroup, Section)
                  er nyere enn tokenets utstedelsestidspunkt. Hvis ja: ville gitt 409 i deployed miljø.
                </p>
                <button
                  className='btn btn-danger'
                  onClick={checkTokenRefresh}
                  disabled={loading !== null}
                >
                  {loading === 'check' ? 'Sjekker...' : 'Check Token Refresh'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className='card'>
          <div className='card-header d-flex justify-content-between align-items-center'>
            <h5 className='mb-0'>Logg</h5>
            {logs.length > 0 && (
              <button className='btn btn-sm btn-outline-secondary' onClick={() => setLogs([])}>
                Tøm logg
              </button>
            )}
          </div>
          <div className='card-body' style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {logs.length === 0 ? (
              <p className='text-muted mb-0'>Ingen loggmeldinger ennå. Bruk knappene over for å starte.</p>
            ) : (
              <table className='table table-sm table-borderless mb-0'>
                <tbody>
                  {logs.map((entry, i) => (
                    <tr key={i}>
                      <td className='text-muted' style={{ width: '80px', whiteSpace: 'nowrap' }}>
                        {entry.time}
                      </td>
                      <td style={{ width: '180px' }}>
                        <strong>{entry.action}</strong>
                      </td>
                      <td className={statusColor(entry.status)}>
                        {entry.detail}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
};

export default DevTools;
